import { useSelector } from "react-redux";
import React, { useState, useMemo } from "react";
import { filterBoards } from "src/helpers/boards-filter-helper/boards-filter-helper";
import { stages } from "src/types/board";
import { RootState } from "src/redux/store";
import styled from "styled-components";
import {
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
} from "@hello-pangea/dnd";
import Task from "./Task";

interface IBoardProps {
  stage: stages;
  stageId: string;
  key: string;
  callbacks: ((clientId: string) => Promise<void>)[];
  chatCommand: (clientId: string) => void;
}

const Wrapper = styled.div`
  // Wrapping Handle, Area
  display: flex;
  flex-direction: column;
  padding-left: 100px;
`;

const TaskCount = styled.div`
  // Showing how many tasks in the board.
  padding-left: 10px;
`;

const Area = styled.div<{ isOver: boolean; isExpanded: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  align-items: center;
  row-gap: 2px;

  min-height: ${(props) => (props.isExpanded ? "290px" : "200px")};
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  position: relative;
`;

const EmptyState = styled.div`
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
`;

const Board: React.FC<IBoardProps> = ({
  stage,
  stageId,
  callbacks,
  chatCommand,
}) => {
  const [isExpanded] = useState(false);

  const searchValue = useSelector<RootState, string>(
    (state) => state.activeStep.searchValue
  );

  const filteredTasks = useMemo(() => {
    return filterBoards(searchValue, stage.boardColumnNames);
  }, [searchValue, stage.boardColumnNames]);

  if (!stageId) return null ;

  return (
    <Wrapper className="StageCard">
      <h5 className="StageCard__title">{stage?.stageName}</h5>
      <div className="StageCard__status">
        <TaskCount>
          Goal: {Number(stage?.goal) === 1 ? `1 Day` : `${stage?.goal} Days`}{" "}
        </TaskCount>
      </div>
      <div className="StageCard__dropBox">
        <Droppable droppableId={stageId}>
          {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
            <Area
              ref={provided.innerRef}
              {...provided.droppableProps}
              isOver={snapshot.isDraggingOver}
              isExpanded={isExpanded}
            >
              {filteredTasks?.map((task, index) => (
                <Task
                  key={`task-${task.id}-${index}`}
                  id={task.id}
                  index={index}
                  task={task}
                  stageId={
                    stage.id || stage.stageName || `stage-${stage.index}`
                  }
                  stageName={stage.stageName}
                  intakeOrOnboarding={true}
                  callbacks={callbacks}
                  chatCommand={chatCommand}
                />
              ))}
              {filteredTasks.length === 0 && !snapshot.isDraggingOver && (
                <EmptyState />
              )}
              {provided.placeholder}
            </Area>
          )}
        </Droppable>
      </div>
    </Wrapper>
  );
};

export default Board;
